import { KeysToCamelCase, CycleWithRels, TeamWithRels } from '@statsbomb/parachute-types';
import { BaseOption, Nullable, NumberOption } from '@/types/generic';

export const getTeamIdFromCycle = (
  playerCycles: KeysToCamelCase<CycleWithRels>[],
  playerTeams: TeamWithRels[],
  targetCycleId: number,
): Nullable<number> => {
  const hasMatchingCycle = playerCycles.find(({ cycle }) => cycle.cycleId === targetCycleId);
  if (!hasMatchingCycle) return null;

  const [targetCycleTeamId] = playerTeams
    .filter(playerTeam => playerTeam.cycle_ids.includes(targetCycleId))
    .map(({ team_id: teamId }) => teamId);

  return targetCycleTeamId;
};

export const sortCyclesBySeasonAndComp = (cycles: KeysToCamelCase<CycleWithRels>[]) => {
  const { leagues, otherComps } = cycles.reduce<{
    leagues: KeysToCamelCase<CycleWithRels>[];
    otherComps: KeysToCamelCase<CycleWithRels>[];
  }>(
    (acc, cycle) => {
      if (cycle.competition.typeId === 1) {
        return {
          ...acc,
          leagues: [...acc.leagues, cycle],
        };
      }
      return {
        ...acc,
        otherComps: [...acc.otherComps, cycle],
      };
    },
    { leagues: [], otherComps: [] },
  );

  return [
    ...leagues.sort((a, b) => b.season.name.localeCompare(a.season.name)),
    ...otherComps.sort((a, b) => b.season.name.localeCompare(a.season.name)),
  ];
};

export const getDistinctOrderedCompetitionOptions = (
  orderedCycles: KeysToCamelCase<CycleWithRels>[],
  filteredCompetitionOptions: NumberOption[],
) =>
  orderedCycles.reduce((acc, cycle) => {
    const currOption = filteredCompetitionOptions.find(option => cycle.competition.competitionId === option.value);
    if (!acc.some(item => item.label === cycle.competition.name) && currOption) {
      return [...acc, currOption];
    }
    return acc;
  }, [] as BaseOption<number>[]);

export const getOrderedTeamOptions = (
  cycles: KeysToCamelCase<CycleWithRels>[],
  teamOptions: NumberOption[],
  playerTeams: TeamWithRels[],
) => {
  const teamIds = [
    ...new Set(cycles.map(cycle => getTeamIdFromCycle(cycles, playerTeams, cycle.cycle.cycleId))),
  ].filter(Boolean);

  return [...teamOptions].sort((a, b) => teamIds.indexOf(a.value) - teamIds.indexOf(b.value));
};

export const sortCompetitionSeasonNamesBySeasonDescCompAsc = (arr: { competitionName: string; seasonName: string }[]) =>
  arr.sort((a, b) => {
    // sort by season name descending, then competition name ascending
    const seasonA = a.seasonName;
    const seasonB = b.seasonName;
    const seasonCmp = seasonB.localeCompare(seasonA, undefined, { sensitivity: 'accent' }); // descending
    if (seasonCmp !== 0) return seasonCmp;
    return a.competitionName.localeCompare(b.competitionName, undefined, { sensitivity: 'accent' }); // ascending
  });
